import React from 'react';

import { Report } from 'aws-sdk/clients/d3aservice';
import { Container, CopyToClipboard, Header, KeyValuePairs, SpaceBetween, Tabs } from '@amzn/awsui-components-react-v3';
import { StatusIcon } from 'src/components/permissions/myDatasets/statusIcon';

export interface ReportDetailProps {
  setContentType: any;
  activeGroup: string;
  activeWorkspace?: any;
  setNotifications?: any;
  report: Report;
  s3Urls: string[];
}

export const ReportDetail = (props: ReportDetailProps) => {
  const detailContainer = () => {
    return (
      <Container header={<Header variant='h2'>Report details</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Report ID',
              value: props.report.reportId,
            },
            {
              label: 'Status',
              value: StatusIcon(props.report.status),
            },
            {
              label: 'Status reason',
              value: props.report.statusReason,
            },
            {
              label: 'Report name',
              value: props.report.name,
            },
            {
              label: 'Report description',
              value: props.report.description,
            },
            {
              label: 'Owner ID',
              value: props.report.ownerId,
            },
            {
              label: 'Created by',
              value: props.report.createdBy,
            },
            {
              label: 'Created on',
              value: props.report.createdOn,
            },
            {
              label: 'Generation time (mins)',
              value: props.report.generationTimeMins,
            },
            {
              label: 'Generation start time',
              value: props.report.generationStartTime,
            },
            {
              label: 'Generation end time',
              value: props.report.generationEndTime,
            },
          ]}
        />
      </Container>
    );
  };

  const configContainer = () => {
    return (
      <Container header={<Header variant='h2'>Report configuration</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Output types',
              value: props.report.config.outputTypes?.join(', '),
            },
            props.report.config.accessGrantFilter?.dgsa
              ? {
                  label: 'Governance tag',
                  value: props.report.config.accessGrantFilter?.dgsa,
                }
              : undefined,
            props.report.config.accessGrantFilter?.accessProvider
              ? {
                  label: 'Access provider',
                  value: props.report.config.accessGrantFilter?.accessProvider,
                }
              : undefined,
            props.report.config.accessGrantFilter?.resourceArn
              ? {
                  label: 'Resource ARN',
                  value: props.report.config.accessGrantFilter?.resourceArn,
                }
              : undefined,
            props.report.config.accessGrantFilter?.status
              ? {
                  label: 'Grant status',
                  value: props.report.config.accessGrantFilter?.status,
                }
              : undefined,
            props.report.config.accessGrantFilter?.createDateRange?.startDate
              ? {
                  label: 'Include access grants with decision date after',
                  value: props.report.config.accessGrantFilter?.createDateRange?.startDate,
                }
              : undefined,
            props.report.config.accessGrantFilter?.createDateRange?.startDate
              ? {
                  label: 'Include access grants with decision date before',
                  value: props.report.config.accessGrantFilter?.createDateRange?.endDate,
                }
              : undefined,
          ].filter((item) => item != undefined)}
        />
      </Container>
    );
  };

  const s3MetadataContainer = () => {
    return (
      <Container header={<Header variant='h2'>S3 artifacts</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Raw access grants S3 path',
              value: (
                <CopyToClipboard
                  variant={'inline'}
                  textToCopy={props.report.s3Metadata.d3aAccessGrantsExportS3Path}
                  copySuccessText={'Copied S3 path'}
                  copyErrorText={'Failed to copy S3 path'}
                />
              ),
            },
            {
              label: 'Staged report S3 path',
              value: (
                <CopyToClipboard
                  variant={'inline'}
                  textToCopy={Object.values(props.report.s3Metadata.d3aStagedS3PathsMap).join(', ')}
                  copySuccessText={'Copied S3 path'}
                  copyErrorText={'Failed to copy S3 path'}
                />
              ),
            },
            {
              label: 'Processed report S3 paths',
              value: (
                <CopyToClipboard
                  variant={'inline'}
                  textToCopy={Object.values(props.report.s3Metadata.d3aProcessedS3PathsMap).join(', ')}
                  copySuccessText={'Copied S3 path'}
                  copyErrorText={'Failed to copy S3 path'}
                />
              ),
            },
          ].filter((item) => item != undefined)}
        />
      </Container>
    );
  };

  return (
    <Tabs
      tabs={[
        {
          id: 'detail',
          label: 'Details',
          content: detailContainer(),
        },
        {
          id: 'config',
          label: 'Configuration',
          content: configContainer(),
        },
        {
          id: 'artifacts',
          label: 'Artifacts',
          content: (
            <SpaceBetween size={'l'} direction={'vertical'}>
              {s3MetadataContainer()}
            </SpaceBetween>
          ),
        },
      ]}
    />
  );
};
